import { useState, useEffect, useCallback, useRef } from 'react'
import { evalApiClient } from '@/lib/api/eval.client'
import type { TriggerEvalRunPayload } from '@/lib/api/eval.client'
import { EvalRunStatus, EvalRunScope } from '@super/shared-types'
import type { EvalRun } from '@super/shared-types'

const POLL_INTERVAL_MS = 3_000
const TERMINAL_STATUSES: EvalRunStatus[] = [
  EvalRunStatus.COMPLETED,
  EvalRunStatus.FAILED,
]

interface UseEvalRunsResult {
  runs: EvalRun[]
  total: number
  isFetching: boolean
  error: string | null
  offset: number
  limit: number
  setOffset: (offset: number) => void
  triggerRun: (payload: TriggerEvalRunPayload) => Promise<void>
  refetch: () => void
}

export function useEvalRuns(
  opts: {
    companyId?: string
    limit?: number
  } = {},
): UseEvalRunsResult {
  const { companyId, limit = 20 } = opts
  const [runs, setRuns] = useState<EvalRun[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fetchTick, setFetchTick] = useState(0)
  const pollingRunIds = useRef<Set<string>>(new Set())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    let cancelled = false
    setIsFetching(true)
    setError(null)

    evalApiClient
      .listRuns({ companyId, limit, offset })
      .then(response => {
        if (cancelled) return
        if (response) {
          setRuns(response.data)
          setTotal(response.pagination.total)
        } else {
          setError('Failed to load eval runs')
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })

    return () => {
      cancelled = true
    }
  }, [companyId, limit, offset, fetchTick])

  // Poll active runs every 3s until they reach a terminal status
  useEffect(() => {
    if (pollingRunIds.current.size === 0) return

    intervalRef.current = setInterval(async () => {
      const ids = Array.from(pollingRunIds.current)
      const updates = await Promise.all(
        ids.map(id => evalApiClient.getRunById(id)),
      )

      setRuns(prev => {
        let changed = false
        const next = prev.map(run => {
          const update = updates.find(u => u?.id === run.id)
          if (update && update.status !== run.status) {
            changed = true
            return update
          }
          return run
        })
        return changed ? next : prev
      })

      // Stop polling runs that have reached a terminal status
      updates.forEach(update => {
        if (
          update &&
          TERMINAL_STATUSES.includes(update.status as EvalRunStatus)
        ) {
          pollingRunIds.current.delete(update.id)
        }
      })

      if (pollingRunIds.current.size === 0 && intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [runs])

  const triggerRun = useCallback(async (payload: TriggerEvalRunPayload) => {
    const response = await evalApiClient.triggerRun(payload)
    if (!response?.runId) return

    const runId = response.runId
    pollingRunIds.current.add(runId)

    // Immediately show a pending placeholder so the user sees it right away
    const now = new Date().toISOString()
    const optimisticRun: EvalRun = {
      id: runId,
      scope: payload.companyId ? EvalRunScope.COMPANY : EvalRunScope.GLOBAL,
      companyId: payload.companyId ?? null,
      promptType: payload.promptType ?? null,
      model: '',
      status: EvalRunStatus.PENDING,
      totalCases: 0,
      passedCases: 0,
      failedCases: 0,
      triggeredBy: '',
      createdAt: now,
      updatedAt: now,
    }
    setRuns(prev => [optimisticRun, ...prev])
    setTotal(t => t + 1)

    // Replace optimistic row with real data once available
    const newRun = await evalApiClient.getRunById(runId)
    if (newRun) {
      setRuns(prev => prev.map(r => (r.id === runId ? newRun : r)))
    }
  }, [])

  const refetch = useCallback(() => setFetchTick(t => t + 1), [])

  return {
    runs,
    total,
    isFetching,
    error,
    offset,
    limit,
    setOffset,
    triggerRun,
    refetch,
  }
}
